import { TextField } from "@mui/material";
import { useState } from "react";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useReviewUser } from "./api";
import type { User } from "../../types/user";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";

interface Props {
  user: User | null;
  approved: boolean;
  onClose: () => void;
  onReviewed?: () => void;
}

export function ReviewUserDialog({ user, approved, onClose, onReviewed }: Props) {
  const review = useReviewUser();
  const notify = useNotify();
  const [reason, setReason] = useState("");

  return (
    <ConfirmDialog
      open={user !== null}
      title={`${approved ? "Approve" : "Decline"} ${user?.email}?`}
      message={
        approved
          ? "They will be able to sign in and use the app."
          : "They will be told their registration was declined."
      }
      confirmLabel={approved ? "Approve" : "Decline"}
      destructive={!approved}
      busy={review.isPending}
      onClose={onClose}
      onConfirm={async () => {
        if (!user) return;
        try {
          // The reason is only sent on a rejection; approval posts an empty comment.
          await review.mutateAsync({
            userId: user.id,
            approved,
            comment: approved ? "" : reason,
          });
          notify.success(approved ? "Registration approved" : "Registration declined");
          onReviewed?.();
        } catch (error) {
          notify.error(
            errorMessageFor(error) ??
              `Could not ${approved ? "approve" : "decline"} the registration.`,
          );
        } finally {
          onClose();
        }
      }}
    >
      {!approved && (
        <TextField
          label="Reason (shown to them)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          fullWidth
          multiline
          minRows={2}
          margin="normal"
        />
      )}
    </ConfirmDialog>
  );
}
